import { format, formatISO, isDate, isValid, parseISO, parseJSON, startOfDay } from 'date-fns';

import { DATE_FORMAT } from 'common/constants';
import { addDuration } from 'common/utils';

import { type CustomerDetails } from 'api/customers';
import type { LicenseDetails, LicenseFile, LicenseListEntry } from 'api/licenses';
import { type ProductConfiguration } from 'api/productConfigurations';
import { Feature, type Product } from 'api/products';

export const PLATFORMS = {
    macos: 'MAC',
    linux: 'Linux',
    win32: 'Windows',
};

export function isDateTimeEarlierThanNow(dateTimeStr: string): boolean {
    if (!dateTimeStr) return false;

    const isoParsed = parseISO(dateTimeStr);
    const parsed = isValid(isoParsed) ? isoParsed : parseJSON(dateTimeStr);

    if (!isValid(parsed)) return false;

    return parsed.getTime() < Date.now();
}

export function isDateEarlierThanToday(dateTimeStr: string): boolean {
    if (!dateTimeStr) return false;

    const isoParsed = parseISO(dateTimeStr);
    const parsed = isValid(isoParsed) ? isoParsed : parseJSON(dateTimeStr);

    if (!isValid(parsed)) return false;

    const inputStart = startOfDay(parsed).getTime();
    const todayStart = startOfDay(new Date()).getTime();

    return inputStart < todayStart;
}

export function formatDate(input?: string): string {
    if (input != null) {
        return format(parseJSON(input), DATE_FORMAT);
    }

    return '';
}

export function creationDate(licenseOrActivation: LicenseDetails | LicenseFile) {
    if (licenseOrActivation.creation_date) {
        return format(parseJSON(licenseOrActivation.creation_date), DATE_FORMAT);
    }
    return 'N/A';
}

export function expiryDateString(license: LicenseListEntry | LicenseDetails, expirationDate?: string): string {
    // No license files -> no expiry string
    if (!license || license.licenseFilesCount === 0) {
        return '';
    }

    // No date provided
    if (!expirationDate) {
        return '';
    }

    // Try ISO first, fall back to JSON date parsing
    const isoParsed = parseISO(expirationDate);
    const parsedDate = isValid(isoParsed) ? isoParsed : parseJSON(expirationDate);

    // If still invalid, bail
    if (!isValid(parsedDate)) {
        return '';
    }

    // Format once we know it's valid
    const expiry = format(parsedDate, DATE_FORMAT);

    // Your helper presumably checks if the date is strictly earlier than today (date-only)
    const expired = isDateEarlierThanToday(expirationDate);
    // If expired -> "Expired", else -> "Expiring"
    return expired ? `(Expired on ${expiry})` : `(Expiring on ${expiry})`;
}

export function expirationDate(license: LicenseListEntry | LicenseDetails, expirationDate: string) {
    if (license.licenseFilesCount === 0) {
        return 'N/A';
    }

    if (expirationDate) {
        return format(
            isValid(parseISO(expirationDate)) ? parseISO(expirationDate) : parseJSON(expirationDate),
            DATE_FORMAT
        );
    }
    return 'unlimited';
}

export function licenseFileExpirationDate(expirationDate: string) {
    if (expirationDate) {
        return format(parseISO(expirationDate), DATE_FORMAT);
    }
    return 'unlimited';
}

export function calcExpirationDate(referenceDate: Date | string, configuration: ProductConfiguration) {
    let expirationDate = addDuration(referenceDate, configuration.validity, configuration.validityUnit);

    const product = configuration.product;
    if (product.validity_of_activation) {
        const technicalExpirationDate = addDuration(referenceDate, product.validity_of_activation, 'd');
        if (technicalExpirationDate) {
            expirationDate =
                !expirationDate || technicalExpirationDate < expirationDate ? technicalExpirationDate : expirationDate;
        }
    }

    return expirationDate;
}

export function hasSystemId(productConfiguration: ProductConfiguration) {
    const hiSystemId = productConfiguration?.product?.hardwareBindings?.find(({ type }) => type === 'System ID')?.id;
    const hiMbcSystemId = productConfiguration?.product?.hardwareBindings?.find(
        ({ type }) => type === 'MBC System ID'
    )?.id;
    return (
        productConfiguration?.hardwareBinding === hiSystemId || productConfiguration?.hardwareBinding === hiMbcSystemId
    );
}

export function hasMacAddress(productConfiguration: ProductConfiguration) {
    const hiMacId = productConfiguration?.product?.hardwareBindings?.find(
        ({ type }) => type === 'MAC Address' || type === 'Visia Legacy MAC Address'
    )?.id;
    return productConfiguration?.hardwareBinding === hiMacId;
}

export function hasNoneHardwareBinding(productConfiguration: ProductConfiguration) {
    const id = productConfiguration?.product?.hardwareBindings?.find(({ type }) => type === 'None')?.id;
    return productConfiguration?.hardwareBinding === id;
}

export function hasSmartDrLegacySiteId(productConfiguration: ProductConfiguration) {
    const id = productConfiguration?.product?.hardwareBindings?.find(
        ({ type }) => type === 'SmartDR Legacy SiteID'
    )?.id;
    return productConfiguration?.hardwareBinding === id;
}

export function hasInficryptSystemId(productConfiguration: ProductConfiguration) {
    const id = productConfiguration?.product?.hardwareBindings?.find(({ type }) => type === 'Inficrypt System Id')?.id;
    return productConfiguration?.hardwareBinding === id;
}

export function getNumberOfMacAddresses(productConfiguration: ProductConfiguration) {
    return hasMacAddress(productConfiguration) ? productConfiguration.product.numberOfMacAddresses : 0;
}

function removeTrailingSemicolon(value: string): string {
    if (value.endsWith(';')) {
        return value.slice(0, -1);
    }
    return value;
}

export function getHardwareIdentifiers(productConfiguration: ProductConfiguration, values: any) {
    if (hasMacAddress(productConfiguration)) return getMacAddresses(productConfiguration, values);
    if (hasSystemId(productConfiguration) || hasInficryptSystemId(productConfiguration))
        return [removeTrailingSemicolon(values.system_id)];
    if (hasSmartDrLegacySiteId(productConfiguration)) return [values.site_id];
    if (hasNoneHardwareBinding(productConfiguration)) return [];
}
function getMacAddresses(productConfiguration: ProductConfiguration, values: any) {
    const numberOfMacAddresses = getNumberOfMacAddresses(productConfiguration);
    return Array(numberOfMacAddresses)
        .fill(null)
        .map((_, index) => values[`mac_address_${index}`])
        .filter(m => m && m.length > 0);
}

export function getPlatforms(values: any) {
    let platforms = '';
    Object.entries(PLATFORMS).forEach(([k, _v]) => {
        if (values[`platform_${k}`]) {
            platforms += `${k} `;
        }
    });
    return platforms.trim();
}

export function isApplication(productConfiguration: Pick<ProductConfiguration, 'product'>, currentProduct: Product) {
    if (productConfiguration?.product) {
        return productConfiguration.product.is_application;
    }

    return currentProduct?.is_application;
}

function usesMeVisLabLicenseGenerator(productConfiguration: ProductConfiguration) {
    const hardwareBinding = productConfiguration.product.hardwareBindings.find(
        hb => hb.id === productConfiguration.hardwareBinding
    );

    return hardwareBinding.licenseGenerator.name === 'MeVisLab';
}

export function licenseGenerateCanSignFiles(productConfiguration: ProductConfiguration) {
    return usesMeVisLabLicenseGenerator(productConfiguration);
}

export function licenseGenerateSupportsMultiplePlatforms(productConfiguration: ProductConfiguration) {
    return usesMeVisLabLicenseGenerator(productConfiguration);
}

export function getLicenseById(licenses: LicenseListEntry[], id: number) {
    return licenses?.find(l => l.id === id);
}

export function getLicenseLabel(licenses: LicenseListEntry[], id: number) {
    const license = getLicenseById(licenses, id);
    if (license) {
        return `${license.configuration}`;
    }
    return '';
}

export function getCustomerById(customers: CustomerDetails[], id: number) {
    return customers?.find(c => c.id === id);
}

export function getCustomerLabel(customers: CustomerDetails[], id: number) {
    const customer = getCustomerById(customers, id);
    if (customer) {
        return `${customer.full_name} (${customer.email})`;
    }
    return '';
}

export function getCustomerOwnerId(customers: CustomerDetails[], id: number) {
    const customer = getCustomerById(customers, id);
    return customer?.owner_id || '';
}

export function getDateFromDateTimeObject(dateTimeValue: any): any {
    if (dateTimeValue === null || !isDate(dateTimeValue)) {
        // only handle real Date objects
        return dateTimeValue;
    }
    return formatISO(dateTimeValue, { representation: 'date' });
}

export function getDateTimeObjectFromDate(dateString: string) {
    if (dateString === null) {
        return null;
    }
    if (dateString.length !== 10) {
        return dateString;
    }

    return parseISO(dateString);
}

export function getValueOrFallback(value: any, fallback: any): any {
    return value || fallback;
}

export function isNoneHardwareBinding(currentProduct: Product, hardwareBindingId: number) {
    return ['None', 'None (MeVisLab)'].includes(
        currentProduct.hardwareBindings?.find(({ id }) => id === hardwareBindingId)?.type
    );
}

type LicenseWithSerialNumber = { id: number; serial_number: string; hardwareBinding: string };
export function licenseWithHWBindingExists(
    licenses: LicenseWithSerialNumber[] | Record<string, any>,
    serialNumber: string
) {
    if (!serialNumber) {
        return false;
    }
    const licensesWithSerialNumber = licenses.filter((l: LicenseWithSerialNumber) => l.serial_number === serialNumber);

    return licensesWithSerialNumber.some((l: LicenseWithSerialNumber) => {
        return !['None', 'None (MeVisLab)'].includes(l.hardwareBinding);
    });
}

export function isRuntimeFeature(f: Feature) {
    return f.featureType === 'Runtime';
}
